import { useMemo, useState } from "react";
import { geoMercator, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import topo from "@/data/india-topo.json";

const WIDTH = 640;
const HEIGHT = 720;

const geo = feature(topo, topo.objects.india);
const projection = geoMercator().fitExtent(
  [
    [24, 24],
    [WIDTH - 24, HEIGHT - 24],
  ],
  geo
);
const pathGen = geoPath(projection);
const STATE_PATHS = geo.features.map((f, i) => ({ d: pathGen(f), key: i }));

const TYPE_COLOR = { ocean: "#00E5FF", agriculture: "#10B981" };
const STATUS_COLOR = { alert: "#F43F5E", watch: "#FBBF24", nominal: null };

export default function IndiaMap({ points = [], activeLayer = "all", onSelect }) {
  const [hover, setHover] = useState(null);

  const nodes = useMemo(() => {
    return points
      .map((p) => {
        const xy = projection([p.lon, p.lat]);
        if (!xy) return null;
        return { ...p, xPct: (xy[0] / WIDTH) * 100, yPct: (xy[1] / HEIGHT) * 100 };
      })
      .filter(Boolean)
      .filter((p) => activeLayer === "all" || p.type === activeLayer);
  }, [points, activeLayer]);

  return (
    <div className="relative w-full" style={{ aspectRatio: `${WIDTH} / ${HEIGHT}` }}>
      {/* soft radial glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 40%, rgba(0,229,255,0.10), transparent 70%)",
        }}
      />
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="absolute inset-0 h-full w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        {STATE_PATHS.map((s) => (
          <path
            key={s.key}
            d={s.d}
            fill="rgba(15,23,42,0.55)"
            stroke="rgba(0,229,255,0.22)"
            strokeWidth="0.8"
          />
        ))}
      </svg>

      {/* interactive nodes */}
      {nodes.map((n) => {
        const color = TYPE_COLOR[n.type] || "#00E5FF";
        const ring = STATUS_COLOR[n.status] || color;
        return (
          <button
            key={n.id}
            data-testid={`map-node-${n.id}`}
            type="button"
            onMouseEnter={() => setHover(n.id)}
            onMouseLeave={() => setHover((h) => (h === n.id ? null : h))}
            onClick={() => onSelect && onSelect(n)}
            className="absolute -translate-x-1/2 -translate-y-1/2 focus:outline-none"
            style={{ left: `${n.xPct}%`, top: `${n.yPct}%` }}
            aria-label={n.name}
          >
            <span className="relative flex h-3 w-3 items-center justify-center">
              <span
                className="map-node-ring absolute inline-flex h-3 w-3 rounded-full"
                style={{ backgroundColor: ring, opacity: 0.5 }}
              />
              <span
                className="relative inline-flex h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: color, boxShadow: `0 0 10px ${color}` }}
              />
            </span>

            {hover === n.id && (
              <div
                className="glass absolute bottom-5 left-1/2 z-20 w-48 -translate-x-1/2 rounded-lg p-3 text-left"
                data-testid={`map-tooltip-${n.id}`}
              >
                <p className="font-heading text-sm font-semibold text-white">{n.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400">
                  {n.region}
                </p>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-slate-400">{n.metric}</span>
                  <span
                    className="font-mono text-xs font-semibold"
                    style={{ color }}
                  >
                    {n.value}
                  </span>
                </div>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
